/**
 * Bin refill status logic for the Assembly Line Dashboard.
 *
 * A component bin is considered adequately stocked when remaining quantity is at
 * least BIN_SAFETY_MULTIPLIER × the per-unit requirement (default 4×).
 * Below that threshold the tile/station is flagged for refill.
 */
export const BIN_SAFETY_MULTIPLIER = 4;

export type BinStatusLevel = 'ok' | 'low';

export function getMinimumSafeStock(requiredPerUnit: number): number {
  if (requiredPerUnit <= 0) return 0;
  return requiredPerUnit * BIN_SAFETY_MULTIPLIER;
}

export function getBinStatus(remainingInBin: number, requiredPerUnit: number): BinStatusLevel {
  if (requiredPerUnit <= 0) return 'ok';
  return remainingInBin >= getMinimumSafeStock(requiredPerUnit) ? 'ok' : 'low';
}

export function stationNeedsRefill(
  components: Array<{ remainingInBin: number; requiredPerUnit: number }>
): boolean {
  return components.some(
    (c) => getBinStatus(c.remainingInBin, c.requiredPerUnit) === 'low'
  );
}

export interface BinStatusColors {
  dot: string;
  border: string;
  cardBg: string;
  text: string;
  leftAccent: string;
}

export function getBinStatusColors(status: BinStatusLevel): BinStatusColors {
  if (status === 'ok') {
    return {
      dot: 'bg-emerald-500',
      border: 'border-primary-200',
      cardBg: 'bg-white',
      text: 'text-emerald-600',
      leftAccent: 'before:bg-emerald-500',
    };
  }
  return {
    dot: 'bg-red-500',
    border: 'border-red-300',
    cardBg: 'bg-red-50/40',
    text: 'text-red-600',
    leftAccent: 'before:bg-red-500',
  };
}

export function formatCycleTime(seconds: number): string {
  if (seconds <= 0) return '—';
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  if (mins === 0) return `${secs}sec`;
  return `${mins}min ${secs}sec`;
}
